import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDeviceCommandHistory } from "@/hooks/useDeviceCommandHistory";
import { format } from "date-fns";
import { CheckCircle, XCircle, Clock } from "lucide-react";

interface CommandHistoryTableProps {
  deviceId: string;
}

export const CommandHistoryTable = ({ deviceId }: CommandHistoryTableProps) => {
  const { history, isLoading } = useDeviceCommandHistory(deviceId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">Loading command history...</div>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">No command history found</div>
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-3 w-3 text-green-500" />;
      case "failed":
        return <XCircle className="h-3 w-3 text-red-500" />;
      case "pending":
        return <Clock className="h-3 w-3 text-blue-500" />;
      default:
        return null;
    }
  };

  const completedCommands = history.filter(item => item.status === "completed");
  const failedCommands = history.filter(item => item.status === "failed");
  const pendingCommands = history.filter(item => item.status === "pending");

  const renderTable = (commands: typeof history) => {
    if (commands.length === 0) {
      return (
        <div className="flex items-center justify-center p-4">
          <div className="text-muted-foreground text-sm">No commands found</div>
        </div>
      );
    }

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="py-1.5 text-xs">Command</TableHead>
            <TableHead className="py-1.5 text-xs">Timestamp</TableHead>
            <TableHead className="py-1.5 text-xs">Error</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {commands.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="py-1.5 font-medium text-sm">
                <div className="flex items-center space-x-1.5">
                  {getStatusIcon(item.status)}
                  <span>{item.name}</span>
                </div>
              </TableCell>
              <TableCell className="py-1.5 text-sm text-muted-foreground">
                {format(new Date(item.timestamp), "M/d h:mm a")}
              </TableCell>
              <TableCell className="py-1.5">
                {item.error ? (
                  <span className="text-red-600 text-xs">{item.error}</span>
                ) : (
                  <span className="text-muted-foreground text-sm">-</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="space-y-2">
      <h3 className="text-base font-semibold">Command History</h3>
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="h-8">
          <TabsTrigger value="all" className="text-xs px-2 py-1">All ({history.length})</TabsTrigger>
          <TabsTrigger value="completed" className="text-xs px-2 py-1">Completed ({completedCommands.length})</TabsTrigger>
          <TabsTrigger value="pending" className="text-xs px-2 py-1">Pending ({pendingCommands.length})</TabsTrigger>
          <TabsTrigger value="failed" className="text-xs px-2 py-1">Failed ({failedCommands.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-2">
          {renderTable(history)}
        </TabsContent>

        <TabsContent value="completed" className="mt-2">
          {renderTable(completedCommands)}
        </TabsContent>

        <TabsContent value="pending" className="mt-2">
          {renderTable(pendingCommands)}
        </TabsContent>

        <TabsContent value="failed" className="mt-2">
          {renderTable(failedCommands)}
        </TabsContent>
      </Tabs>
    </div>
  );
};
